<template>
    <div class="rc-select">
        <slot name="label">
            <label
                :id="`${input_id}-label`"
                class="input-label"
                :class="{ 'required': requiredBool, 'underlined': !outlined }"
            >
                {{ label }}
                <span
                    v-if="requiredBool"
                    class="required-indicator"
                    aria-hidden="true"
                >*</span>
            </label>
        </slot>

        <div
            v-if="!multiple"
            class="select-container"
            :class="{
                'select-disabled': disabled,
                'select-error': hasError,
                'select-outlined': outlined,
                'select-underlined': !outlined,
                'select-dropdown-open': dropdown_open,
                'select-dropdown-open-top': dropdown_open && dropdownPlacement === 'top',
                'select-dropdown-open-bottom': dropdown_open && dropdownPlacement !== 'top'
            }"
        >
            <button
                type="button"
                ref="selectToggle"
                class="form-select-button"
                :class="{ 'has-clear-button': showClearButton }"
                :tabindex="disabled ? -1 : 0"
                :aria-expanded="dropdown_open"
                :aria-haspopup="'listbox'"
                :aria-labelledby="`${input_id}-label`"
                :aria-describedby="describedByIds"
                :aria-invalid="hasError"
                :disabled="disabled"
                @click="toggleDropdown"
                @keydown="onSelectKeydown"
                @blur="onSelectBlur"
                @focus="onSelectFocus"
            >
                <span
                    v-if="!htmlItems"
                    class="select-value"
                    :class="{ 'placeholder': selectValueIsPlaceholder }"
                >
                    {{ selectDisplayValue }}
                </span>
                <span
                    v-else
                    class="select-value"
                    :class="{ 'placeholder': selectValueIsPlaceholder }"
                    v-html="selectDisplayValue"
                >
                </span>

                <button
                    v-if="showClearButton"
                    type="button"
                    class="select-clear-button"
                    :aria-label="`Clear ${label}`"
                    :disabled="disabled || requiredBool"
                    @click.stop="clearSelection"
                    @keydown="onClearKeydown"
                    @mousedown.prevent
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M18 6L6 18M6 6l12 12"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>

                <div
                    class="select-arrow"
                    aria-hidden="true"
                >
                    <svg
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                        :class="{ 'rotated': dropdown_open }"
                    >
                        <path
                            d="M1 1L6 6L11 1"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
            </button>

            <div
                v-if="dropdown_open"
                class="select-dropdown"
                :class="{ 'select-dropdown-top': dropdownPlacement === 'top' }"
                role="listbox"
                :aria-labelledby="`${input_id}-label`"
            >
                <div
                    v-for="(option, index) in items"
                    :key="getOptionValue(option)"
                    :ref="`selectOption-${index}`"
                    class="select-option"
                    :class="{
                        'selected': selected_value === getOptionValue(option),
                        'focused': focused_option_index === index
                    }"
                    role="option"
                    :tabindex="dropdown_open ? (focused_option_index === index ? 0 : -1) : -1"
                    :aria-selected="selected_value === getOptionValue(option)"
                    :aria-labelledby="`${input_id}-select-option-${index}`"
                    @click="selectOption(getOptionValue(option))"
                    @mousedown.prevent
                    @keydown="onSelectOptionKeydown($event, getOptionValue(option), index)"
                    @blur="onSelectOptionBlur"
                >
                    <span
                        v-if="!htmlItems"
                        class="select-option-label"
                        :id="`${input_id}-select-option-${index}`"
                    >
                        {{ getOptionLabel(option) }}
                    </span>
                    <span
                        v-else
                        class="select-option-label"
                        :id="`${input_id}-select-option-${index}`"
                        v-html="getOptionLabel(option)"
                    >
                    </span>
                </div>
            </div>
        </div>

        <div
            v-else
            class="multi-select-container"
            :class="{
                'multi-select-disabled': disabled,
                'multi-select-error': hasError,
                'multi-select-outlined': outlined,
                'multi-select-underlined': !outlined,
                'multi-select-dropdown-open': dropdown_open,
                'multi-select-dropdown-open-top': dropdown_open && dropdownPlacement === 'top',
                'multi-select-dropdown-open-bottom': dropdown_open && dropdownPlacement !== 'top'
            }"
        >
            <div
                role="combobox"
                ref="multiSelectToggle"
                class="multi-select-toggle"
                :tabindex="disabled ? -1 : 0"
                :aria-expanded="dropdown_open"
                :aria-haspopup="'listbox'"
                :aria-labelledby="`${input_id}-label`"
                :aria-describedby="describedByIds"
                :aria-invalid="hasError"
                :title="multiSelectTooltipText"
                @click="toggleDropdown"
                @keydown="onSelectKeydown"
                @blur="onSelectBlur"
                @focus="onSelectFocus"
            >
                <span
                    v-if="!htmlItems"
                    class="multi-select-value"
                    :class="{ 'placeholder': multiSelectValueIsPlaceholder }"
                >
                    {{ multiSelectDisplayValue }}
                </span>
                <span
                    v-else
                    class="multi-select-value"
                    :class="{ 'placeholder': multiSelectValueIsPlaceholder }"
                    v-html="multiSelectDisplayValue"
                >
                </span>

                <div
                    class="multi-select-arrow"
                    aria-hidden="true"
                >
                    <svg
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                        :class="{ 'rotated': dropdown_open }"
                    >
                        <path
                            d="M1 1L6 6L11 1"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
            </div>

            <div
                v-if="dropdown_open"
                class="multi-select-dropdown"
                :class="{ 'multi-select-dropdown-top': dropdownPlacement === 'top' }"
                role="listbox"
                :aria-multiselectable="true"
            >
                <div
                    v-for="(option, index) in items"
                    :key="getOptionValue(option)"
                    class="multi-select-option"
                    :class="{
                        'selected': isOptionSelected(getOptionValue(option)),
                        'focused': focused_option_index === index
                    }"
                    role="option"
                    :tabindex="dropdown_open ? (focused_option_index === index ? 0 : -1) : -1"
                    :aria-selected="isOptionSelected(getOptionValue(option))"
                    :aria-labelledby="`${input_id}-option-${index}`"
                    @click="toggleOption(getOptionValue(option))"
                    @mousedown.prevent
                    @keydown="onOptionKeydown($event, getOptionValue(option), index)"
                    @blur="onSelectOptionBlur"
                    :ref="`option-${index}`"
                >
                    <input
                        type="checkbox"
                        :checked="isOptionSelected(getOptionValue(option))"
                        :disabled="disabled"
                        tabindex="-1"
                        class="multi-select-checkbox"
                        :aria-hidden="true"
                    />
                    <span
                        v-if="!htmlItems"
                        class="multi-select-option-label"
                        :id="`${input_id}-option-${index}`"
                    >
                        {{ getOptionLabel(option) }}
                    </span>
                    <span
                        v-else
                        class="multi-select-option-label"
                        :id="`${input_id}-option-${index}`"
                        v-html="getOptionLabel(option)"
                    >
                    </span>
                </div>
            </div>
        </div>

        <div
            v-if="hasError"
            class="error-message"
            :id="`${input_id}-error`"
            role="alert"
        >
            {{ displayErrorMessage }}
        </div>

        <div
            v-if="(inputHint || $slots.hint) && !hasError"
            class="hint-text"
            :id="`${input_id}-hint`"
        >
            <slot name="hint">
                {{ inputHint }}
            </slot>
        </div>

        <div
            v-if="multiple"
            class="sr-only"
            aria-live="polite"
            aria-atomic="true"
        >
            {{ screen_reader_announcement }}
        </div>
    </div>
</template>

<script>
/**
 * rc-select
 *
 * emits 'input' event with new value on change
 * emits 'validated' event with boolean validity on validation
 * emits 'focus', 'blur', and 'keydown' events as well
 * for select type, emits 'input' event with selected value (or array of values if multiple) on change
 *
 * This is a custom, accessibility-focused form input with no external UI dependencies.
 * Is optimized for AA accessibility compliance.
 *
 * It was originally built for use in public forms where accessibility couldn't be achieved via Vuetify 2 wrappers.
 * However it can also be used in the redchirp app itself.
 * NOTE: this does look different in some (pretty subtle) ways from the Vuetify 2 inputs it replaces.
 * When using this, at least ensure that all other inputs in the same form are also this component, to maintain visual consistency.
 * Additionally, the 'select' functionality is currently rather basic and may need to be enhanced for more complex use cases.
 * Autocomplete reference: https://www.w3.org/TR/WCAG22/#input-purposes
 */

export default {
    name: 'RcSelect',

    props: {
        value: {
            type: [String, Number, Array],
            default: ''
        },
        label: {
            type: String,
            required: true
        },
        hint: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: true
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        /**
         * Can be bool or the string optional/required in the case of custom forms
        */
        required: {
            type: [Boolean, String],
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        errorMessage: {
            type: String,
            default: ''
        },
        items: {
            type: Array,
            default: () => []
        },
        itemValue: {
            type: String,
            default: 'value'
        },
        itemLabel: {
            type: String,
            default: 'text'
        },
        multiple: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: true
        },
        htmlItems: {
            type: Boolean,
            default: false
        },
        dropdownPlacement: {
            type: String,
            default: 'bottom',
            validator: value => ['top', 'bottom'].includes(value)
        },
    },

    data() {
        return {
            input_id: `input-${Math.random().toString(36).substr(2, 9)}`,
            is_focused: false,
            local_value: this.value,
            input_touched: false,
            input_valid: true,
            validation_error_message: '',
            raw_digits: '',
            dropdown_open: false,
            focused_option_index: -1,
            selected_value: this.multiple ?
                (Array.isArray(this.value) ? this.value : []) :
                this.value,
            screen_reader_announcement: ''
        }
    },

    computed: {        
        requiredBool() {
            // can sometimes come in as "optional"
            return this.required === true || this.required === 'required';
        },

        inputHint() {
            return this.hint || ''; // if explicitly provided, that wins out
        },

        hasError() {
            return this.error || (this.errorMessage && this.errorMessage.length > 0)
        },

        displayErrorMessage() {
            return this.errorMessage
        },

        inputClasses() {
            return {
                'input-error': this.hasError,
                'input-disabled': this.disabled,
                'input-readonly': this.readonly,
                'input-underlined': !this.outlined,
            }
        },

        selectedOptionColor() {
            // stricter A11y for public-facing
            return this.$route.meta.isPublic ? '#fff' : '#333'
        },

        selectedOptionBgColor() {
            // stricter A11y for public-facing
            return this.$route.meta.isPublic ? '#767676' : '#eee'
        },

        describedByIds() {
            const ids = []
            const has_hint_content = this.hint || this.$slots.hint
            
            if (has_hint_content && (!this.hasError || this.is_focused)) {
                ids.push(`${this.input_id}-hint`)
            }
            if (this.hasError && !(this.is_focused && has_hint_content)) {
                ids.push(`${this.input_id}-error`)
            }
            return ids.join(' ')
        },

        displayValue() {
            return this.value
        },

        multiSelectDisplayValue() {
            if (!this.multiple) return ''

            if (this.selected_value?.length === 0) {
                return this.placeholder || 'Select options...'
            }

            const selected_labels = this.selected_value.map(value => {
                const option = this.items.find(opt => this.getOptionValue(opt) === value)
                return option ? this.getOptionLabel(option) : value
            })

            return selected_labels.join(', ')
        },

        selectDisplayValue() {
            if (this.multiple) return ''

            if (!this.selected_value || this.selected_value === '') {
                return this.placeholder || 'Select an option...'
            }

            const option = this.items.find(opt => this.getOptionValue(opt) === this.selected_value);
            return option ? this.getOptionLabel(option) : this.selected_value
        },

        selectValueIsPlaceholder() {
            return this.multiple ?
                this.selected_value.length === 0 :
                (!this.selected_value || this.selected_value === '')
        },

        multiSelectValueIsPlaceholder() {
            return this.selected_value.length === 0
        },

        multiSelectTooltipText() {
            if (!this.multiple || this.selected_value.length === 0) {
                return null
            }

            if (this.selected_value.length > 1) {
                const selectedLabels = this.selected_value.map(value => {
                    const option = this.items.find(opt => this.getOptionValue(opt) === value)
                    return option ? this.getOptionLabel(option) : value
                })
                return `Selected: ${selectedLabels.join(', ')}`
            }

            return null
        },

        showClearButton() {
            return this.clearable &&
                !this.requiredBool &&
                !this.disabled &&
                !this.multiple &&
                !this.selectValueIsPlaceholder
        }
    },

    methods: {
        onSelectFocus(event) {
            this.focus();
            this.$emit('focus', event)
        },

        onSelectBlur(event) {
            this.blur();

            setTimeout(() => {
                const focused_element = document.activeElement
                const is_within_component = this.$el && this.$el.contains(focused_element)

                if (!is_within_component) {
                    this.closeDropdown()
                }
            }, 150)

            this.$emit('blur', event)
        },

        focus() {
            this.is_focused = true

            if (this.multiple) {
                this.$refs.multiSelectToggle?.focus()
            } else {
                this.$refs.selectToggle?.focus()
            }
        },

        blur() {
            this.is_focused = false

            if (this.multiple) {
                this.$refs.multiSelectToggle?.blur()
            } else {
                this.$refs.selectToggle?.blur()
            }
        },

        onKeydown(event) {
            this.$emit('keydown', event)
        },

        getOptionValue(option) {
            if (typeof option === 'string' || typeof option === 'number') {
                return option
            }
            return option[this.itemValue]
        },

        getOptionLabel(option) {
            if (typeof option === 'string' || typeof option === 'number') {
                return option
            }
            return option[this.itemLabel]
        },

        toggleDropdown() {
            if (this.disabled) return
            this.dropdown_open = !this.dropdown_open
        },

        closeDropdown() {
            this.dropdown_open = false
            this.focused_option_index = -1
        },

        onSelectKeydown(event) {
            if (this.disabled) return

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                this.toggleDropdown()
                if (this.dropdown_open) {
                    this.$nextTick(() => {
                        this.focusFirstOption()
                    })
                }
            } else if (event.key === 'Escape') {
                this.closeDropdown()
                this.focusToggleButton()
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                if (!this.dropdown_open) {
                    this.dropdown_open = true
                    this.$nextTick(() => {
                        this.focusFirstOption()
                    })
                } else {
                    this.focusNextOption()
                }
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                if (this.dropdown_open) {
                    this.focusPreviousOption()
                }
            }
        },

        focusToggleButton() {
            if (this.multiple) {
                this.$refs.multiSelectToggle?.focus()
            } else {
                this.$refs.selectToggle?.focus()
            }
        },

        onSelectOptionKeydown(event, optionValue, index) {
            if (this.disabled) return

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                this.selectOption(optionValue)
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                this.focusNextOption()
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                this.focusPreviousOption()
            } else if (event.key === 'Escape') {
                event.preventDefault()
                this.closeDropdown()
                this.focusToggleButton()
            } else if (event.key === 'Home') {
                event.preventDefault()
                this.focusFirstOption()
            } else if (event.key === 'End') {
                event.preventDefault()
                this.focusLastOption()
            }
        },

        onSelectOptionBlur() {
            setTimeout(() => {
                const focused_element = document.activeElement
                const isWithinComponent = this.$el && this.$el.contains(focused_element)

                if (!isWithinComponent) {
                    this.closeDropdown()
                }
            }, 150)
        },

        selectOption(value) {
            if (this.disabled) return

            this.selected_value = value
            this.$emit('input', value)
            this.validateSelect()
            this.closeDropdown()
            this.focusToggleButton()
        },

        validateSelect() {
            let has_value;
            if (this.multiple) {
                has_value = Array.isArray(this.selected_value) && this.selected_value.length > 0;
            } else {
                has_value = this.selected_value != null && this.selected_value !== '';
            }
            this.input_valid = has_value || !this.requiredBool;
            this.$emit('validated', this.input_valid);
        },

        toggleOption(value) {
            if (this.disabled) return

            const option = this.items.find(opt => this.getOptionValue(opt) === value)
            const optionLabel = option ? this.getOptionLabel(option) : value

            const index = this.selected_value.indexOf(value)
            if (index > -1) {
                this.selected_value.splice(index, 1)
                this.screen_reader_announcement = `${optionLabel} deselected. ${this.selected_value.length} of ${this.items.length} options selected.`
            } else {
                this.selected_value.push(value)
                this.screen_reader_announcement = `${optionLabel} selected. ${this.selected_value.length} of ${this.items.length} options selected.`
            }

            setTimeout(() => {
                this.screen_reader_announcement = ''
            }, 1000)

            this.$emit('input', this.selected_value)
            this.validateSelect()
        },

        isOptionSelected(value) {
            return this.multiple ? this.selected_value.includes(value) : this.selected_value === value
        },

        onOptionKeydown(event, optionValue, index) {
            if (this.disabled) return

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                this.toggleOption(optionValue)
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                this.focusNextOption()
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                this.focusPreviousOption()
            } else if (event.key === 'Escape') {
                event.preventDefault()
                this.closeDropdown()
                this.focusToggleButton()
            } else if (event.key === 'Home') {
                event.preventDefault()
                this.focusFirstOption()
            } else if (event.key === 'End') {
                event.preventDefault()
                this.focusLastOption()
            }
        },

        focusFirstOption() {
            if (this.items.length > 0) {
                this.focused_option_index = 0
                this.$nextTick(() => {
                    const refName = this.multiple ? `option-0` : `selectOption-0`
                    const firstOption = this.$refs[refName]
                    if (firstOption && firstOption[0]) {
                        firstOption[0].focus()
                    }
                })
            }
        },

        focusLastOption() {
            if (this.items.length > 0) {
                this.focused_option_index = this.items.length - 1
                this.$nextTick(() => {
                    const refName = this.multiple ? `option-${this.items.length - 1}` : `selectOption-${this.items.length - 1}`
                    const lastOption = this.$refs[refName]
                    if (lastOption && lastOption[0]) {
                        lastOption[0].focus()
                    }
                })
            }
        },

        focusNextOption() {
            if (this.items.length > 0) {
                const nextIndex = this.focused_option_index < this.items.length - 1
                    ? this.focused_option_index + 1
                    : 0 // wrap to first
                this.focused_option_index = nextIndex
                this.$nextTick(() => {
                    const refName = this.multiple ? `option-${nextIndex}` : `selectOption-${nextIndex}`
                    const nextOption = this.$refs[refName]
                    if (nextOption && nextOption[0]) {
                        nextOption[0].focus()
                    }
                })
            }
        },

        focusPreviousOption() {
            if (this.items.length > 0) {
                const prevIndex = this.focused_option_index > 0
                    ? this.focused_option_index - 1
                    : this.items.length - 1 // wrap to last
                this.focused_option_index = prevIndex
                this.$nextTick(() => {
                    const refName = this.multiple ? `option-${prevIndex}` : `selectOption-${prevIndex}`
                    const prevOption = this.$refs[refName]
                    if (prevOption && prevOption[0]) {
                        prevOption[0].focus()
                    }
                })
            }
        },

        onClearKeydown(event) {
            if (this.disabled || this.requiredBool) return

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                this.clearSelection()
            }
        },

        clearSelection() {
            if (this.disabled || this.requiredBool) return

            this.selected_value = ''
            this.$emit('input', '')
            this.validateSelect()
            this.focusToggleButton()
        },

        handleClickOutside(event) {
            // close dropdown if clicking outside the entire select component
            if (this.$el && !this.$el.contains(event.target)) {
                this.closeDropdown()
            }
        },
    },

    watch: {
        value(newValue) {
            if (this.multiple) {
                this.selected_value = Array.isArray(newValue) ? newValue : [];
            } else {
                this.selected_value = newValue;
            }
        },

        multiple(is_now_multiple) {
            this.selected_value = is_now_multiple ? [] : '';
            this.$emit('input', this.selected_value);
        }
    },

    mounted() {
        if (this.multiple) {
            this.selected_value = Array.isArray(this.value) ? this.value : [];
        } else {
            this.selected_value = this.value;
        }
        this.validateSelect();
        document.addEventListener('click', this.handleClickOutside);

        if (this.autofocus) {
            this.$nextTick(this.onSelectFocus);
        }
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    }
};
</script>

<style lang="scss" scoped>
.rc-select {
    --text-default: #6b7280;
    --text-dark: #374151;
    --error: #d2233c;
    --dark-grey: #111827;
    --border-color: #c9c9c9;
    --bg-disabled: #f9fafb;
    --primary: var(--v-primary-base, #f24653);
    --color-placeholder: #9ca3af;
    --bg-selected: v-bind(selectedOptionBgColor);
    /* 4.54:1 on white (3:1 minimum) */
    --text-on-selected: v-bind(selectedOptionColor);
    /* 4.54:1 on --bg-selected (4.5:1 minimum) */

    position: relative;
    margin-bottom: 1rem;
}

.select-container {
    position: relative;

    &.select-error .form-select-button {
        border-color: var(--error);

        &:focus {
            border-color: var(--error);
            box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
    }

    &.select-disabled .form-select-button {
        background-color: var(--bg-disabled);
        color: var(--text-default);
        cursor: not-allowed;
    }
}

.input-label {
    display: block;
    font-weight: 500;
    color: var(--text-dark);
    margin-bottom: 0.2rem;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &.underlined {
        margin-bottom: 0;
    }

    &.required {
        .required-indicator {
            color: var(--error);
        }
    }
}

.form-select-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.35rem 2.5rem 0.35rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--text-dark);
    background-color: #ffffff;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    text-align: left;

    &.has-clear-button {
        padding-right: 4.5rem;
    }

    &:focus {
        outline: none;
        border-color: var(--dark-grey);
        box-shadow: 0 0 0 3px rgba(53, 53, 53, 0.1);
    }

    &:disabled {
        cursor: not-allowed;
    }
}

.multi-select-dropdown-open .multi-select-toggle,
.select-dropdown-open .form-select-button {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.select-dropdown-open-top .form-select-button,
.multi-select-dropdown-open-top .multi-select-toggle {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
}

.select-dropdown-open-bottom .form-select-button,
.multi-select-dropdown-open-bottom .multi-select-toggle {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
}

.select-underlined .form-select-button {
    border: none;
    border-bottom: 2px solid var(--border-color);
    border-radius: 0;
    padding-left: 0;
    background-color: transparent;

    &:focus {
        border-bottom-color: var(--dark-grey);
        box-shadow: none;
    }
}

.select-underlined.select-error .form-select-button {
    border-bottom-color: var(--error);

    &:focus {
        border-bottom-color: var(--error);
        box-shadow: none;
    }
}

.select-underlined.select-disabled .form-select-button {
    background-color: transparent;
    border-bottom-color: var(--text-default);
}

.select-value {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.placeholder {
        color: var(--color-placeholder);
    }
}

.select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border: 1px solid var(--border-color);
    border-top: none;
    border-radius: 0 0 0.25rem 0.25rem;
    box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.15), 0 2px 8px -2px rgba(0, 0, 0, 0.1);
    max-height: 260px;
    overflow-y: auto;

    &.select-dropdown-top {
        top: auto;
        bottom: 100%;
        border-top: 1px solid var(--border-color);
        border-bottom: 1px solid var(--border-color);
        border-radius: 0.25rem 0.25rem 0 0;
        box-shadow: 0 -4px 12px -2px rgba(0, 0, 0, 0.15), 0 -2px 8px -2px rgba(0, 0, 0, 0.1);
    }
}

.select-option {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #f3f4f6;
    }

    &.selected {
        background-color: var(--bg-selected);
        font-weight: bold;

        .select-option-label {
            color: var(--text-on-selected);
        }

        &.focused {
            background-color: var(--bg-selected);
        }
    }

    &:focus {
        outline: 2px solid var(--dark-grey);
        outline-offset: -2px;
        background-color: #f3f4f6;
    }
}

.select-option-label {
    color: var(--text-dark);
    flex: 1;
    font-size: 0.95rem;
}

.select-arrow {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--text-default);

    svg {
        transition: transform 0.15s ease;

        &.rotated {
            transform: translateY(-50%) rotate(180deg);
        }
    }
}

.select-clear-button {
    position: absolute;
    top: 50%;
    right: 2.5rem;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 0.25rem;
    cursor: pointer;
    color: var(--primary);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;

    &:hover,
    &:focus {
        background-color: rgba(242, 70, 83, 0.1);
    }

    &:focus {
        outline: 2px solid var(--dark-grey);
        outline-offset: 2px;
    }

    svg {
        width: 20px;
        height: 20px;
    }
}

.multi-select-container {
    position: relative;

    &.multi-select-error .multi-select-toggle {
        border-color: var(--error);

        &:focus {
            border-color: var(--error);
            box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
    }

    &.multi-select-disabled .multi-select-toggle {
        background-color: var(--bg-disabled);
        color: var(--text-default);
        cursor: not-allowed;
    }
}

.multi-select-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.4rem 2.5rem 0.4rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--text-dark);
    background-color: #ffffff;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
        outline: none;
        border-color: var(--dark-grey);
        box-shadow: 0 0 0 3px rgba(53, 53, 53, 0.1);
    }

    &:disabled {
        cursor: not-allowed;
    }
}

.multi-select-underlined .multi-select-toggle {
    border: none;
    border-bottom: 2px solid var(--border-color);
    border-radius: 0;
    padding-left: 0;
    background-color: transparent;

    &:focus {
        border-bottom-color: var(--dark-grey);
        box-shadow: none;
    }
}

.multi-select-underlined.multi-select-error .multi-select-toggle {
    border-bottom-color: var(--error);

    &:focus {
        border-bottom-color: var(--error);
        box-shadow: none;
    }
}

.multi-select-underlined.multi-select-disabled .multi-select-toggle {
    background-color: transparent;
    border-bottom-color: var(--text-default);
}

.multi-select-value {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.placeholder {
        color: var(--color-placeholder);
    }
}

.multi-select-arrow {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--text-default);
    transition: transform 0.2s ease;

    &.rotated {
        transform: translateY(-50%) rotate(180deg);
    }
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border: 1px solid var(--border-color);
    border-top: none;
    border-radius: 0 0 0.25rem 0.25rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    max-height: 200px;
    overflow-y: auto;

    &.multi-select-dropdown-top {
        top: auto;
        bottom: 100%;
        border-top: 1px solid var(--border-color);
        border-bottom: none;
        border-radius: 0.25rem 0.25rem 0 0;
        box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
    }
}

.multi-select-option {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    accent-color: var(--primary);
    transition: background-color 0.15s ease;

    &:hover,
    &.focused {
        background-color: #f3f4f6;
    }

    &.selected {
        background-color: #e5e7eb;
    }

    &.focused.selected {
        background-color: #d1d5db;
    }

    &:focus {
        outline: 2px solid var(--dark-grey);
        outline-offset: -2px;
        background-color: #f3f4f6;
    }
}

.multi-select-checkbox {
    margin-right: 0.5rem;
    pointer-events: none;
}

.multi-select-option-label {
    flex: 1;
}

.error-message {
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: var(--error);
    line-height: 1.25rem;
}

.hint-text {
    margin-top: 2px;
    font-size: 0.875rem;
    color: var(--text-default);
    line-height: 1.25rem;
}

@media (prefers-contrast: high) {
    .form-select-button {
        border-width: 3px;

        &:focus {
            box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.3);
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .rc-select * {
        transition: none !important;
    }
}
</style>